import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import type {
  ClaireBucketChange,
  ClaireBucketShare,
  ClaireBucketWeek,
  ClaireBucketWindow,
  ClaireHandoffWindow,
  ClaireLiveQualityReport,
  ListClaireTurnsInput,
} from '@borradh-workspace/api-client/types';
import { useState } from 'react';
import {
  BUCKETS,
  type BucketKey,
  changeText,
  formatDay,
  formatPct,
  marginText,
  weekLabel,
} from './live-quality-format';
import type { ExplorerRequest } from './turn-explorer';

type Open = (r: ExplorerRequest) => void;

const rangeText = (w: { from: string; to: string }) =>
  w.from === w.to
    ? formatDay(w.from)
    : `${formatDay(w.from)} – ${formatDay(w.to)}`;

const LINK =
  'rounded-sm text-left underline-offset-2 hover:underline focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none';

/** One of the four outcomes, or the booked count beside them. */
function Figure({
  testId,
  label,
  swatch,
  value,
  sub,
  change,
  link,
  onOpen,
}: {
  testId: string;
  label: string;
  swatch?: string;
  value: ClaireBucketShare;
  sub: string;
  change: { text: string; real: boolean; good: boolean | null } | null;
  link: { title: string; filter: ListClaireTurnsInput } | null;
  onOpen: Open;
}) {
  return (
    <div data-testid={testId} className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5 text-sm font-medium">
        {swatch ? (
          <span className={cn('size-2 rounded-[2px]', swatch)} aria-hidden />
        ) : null}
        {label}
      </div>
      <div className="text-3xl font-semibold tracking-tight tabular-nums">
        {formatPct(value.share)}
      </div>
      <div className="text-muted-foreground text-xs">{sub}</div>
      {change ? (
        <div
          className={cn(
            'text-xs',
            change.real && change.good === true && 'text-green-700',
            change.real && change.good === false && 'text-red-700',
            (!change.real || change.good === null) && 'text-muted-foreground'
          )}
        >
          {change.text}
        </div>
      ) : null}
      {link ? (
        <button
          type="button"
          className={cn(LINK, 'mt-1 self-start text-xs')}
          onClick={() => onOpen(link)}
        >
          Conversations
        </button>
      ) : null}
    </div>
  );
}

/** Justified and avoidable, from the live judge, under "Handed to staff". */
function handoffsText(h: ClaireHandoffWindow, minJudged: number): string {
  if (h.judged === 0) return 'justified vs avoidable: not judged yet';
  const pct = (share: number | null) =>
    h.judged >= minJudged && share !== null ? ` (${formatPct(share)})` : '';
  return `${h.right} justified${pct(h.rightShare)} · ${h.unneeded} avoidable${pct(h.unneededShare)}`;
}

function Buckets({
  window,
  change,
  handoffs,
  minJudged,
  onOpen,
}: {
  window: ClaireBucketWindow;
  change: ClaireBucketChange | null;
  handoffs: ClaireHandoffWindow | null;
  minJudged: number;
  onOpen: Open;
}) {
  const live = (title: string, filter: Partial<ListClaireTurnsInput>) => ({
    title: `${title}, ${rangeText(window)}`,
    filter: {
      source: 'live' as const,
      from: window.from,
      to: window.to,
      ...filter,
    } as ListClaireTurnsInput,
  });
  const sub = (key: BucketKey) =>
    [`${window[key].count} of ${window.total}`, marginText(window[key].margin)]
      .filter(Boolean)
      .join(' · ');
  return (
    <div className="flex flex-col gap-5">
      <div
        className="flex h-2.5 overflow-hidden rounded-full"
        role="img"
        aria-label={BUCKETS.map(
          (b) => `${b.label} ${formatPct(window[b.key].share)}`
        ).join(', ')}
      >
        {BUCKETS.map((b) => (
          <div
            key={b.key}
            className={b.fill}
            style={{ width: `${(window[b.key].share ?? 0) * 100}%` }}
          />
        ))}
      </div>
      <div className="grid gap-5 sm:grid-cols-3 lg:grid-cols-5">
        {BUCKETS.map((b) => (
          <Figure
            key={b.key}
            testId={`bucket-${b.key}`}
            label={b.label}
            swatch={b.fill}
            value={window[b.key]}
            sub={
              b.key === 'handedOff' && handoffs
                ? handoffsText(handoffs, minJudged)
                : sub(b.key)
            }
            change={change ? changeText(change[b.key], b.good) : null}
            link={live(b.label, b.filter)}
            onOpen={onOpen}
          />
        ))}
        <div className="border-l pl-5">
          <Figure
            testId="bucket-booked"
            label="Booked in chat"
            value={window.booked}
            sub={`${window.booked.count} · from the booking system, not a judge`}
            change={change ? changeText(change.booked, true) : null}
            link={null}
            onOpen={onOpen}
          />
        </div>
      </div>
      <p className="text-muted-foreground text-xs">
        One bucket per reply. A reply with a serious mistake counts there
        whatever else it did, handed off or not. ± is the 95% range for this
        many replies; a move inside it is noise.
      </p>
    </div>
  );
}

function Weeks({ weeks, onOpen }: { weeks: ClaireBucketWeek[]; onOpen: Open }) {
  const shown = weeks.slice(-8);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-base font-semibold">Week by week</h3>
        <div className="text-muted-foreground flex gap-3 text-xs">
          <span>▲ deploy</span>
          <span className="text-amber-700">◆ judge changed</span>
        </div>
      </div>
      <div
        className="grid gap-3"
        style={{
          gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))`,
        }}
      >
        {shown.map((w) => (
          <div
            key={w.from}
            className="text-muted-foreground h-4 truncate text-[11px]"
            title={[
              ...w.deploys.map((d) => `${formatDay(d.day)}: ${d.label}`),
              ...w.judgeChanges.map((c) => `${formatDay(c.day)}: ${c.label}`),
            ].join('\n')}
          >
            {w.deploys.length ? `▲ ×${w.deploys.length} ` : ''}
            {w.judgeChanges.length ? (
              <span className="text-amber-700">◆</span>
            ) : null}
          </div>
        ))}
      </div>
      <div
        className="grid h-36 items-end gap-3"
        style={{
          gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))`,
        }}
      >
        {shown.map((w) => (
          <button
            type="button"
            key={w.from}
            data-testid={`week-${w.from}`}
            title={`${weekLabel(w)}: ${BUCKETS.map((b) => `${formatPct(w[b.key].share)} ${b.label.toLowerCase()}`).join(', ')} (${w.total} replies)`}
            onClick={() =>
              onOpen({
                title: `Conversations, ${weekLabel(w)}`,
                filter: { source: 'live', from: w.from, to: w.to },
              })
            }
            className={cn(
              'flex h-full flex-col justify-end gap-px rounded-sm hover:opacity-80',
              w.partial && 'opacity-45'
            )}
          >
            {w.total ? (
              [...BUCKETS]
                .reverse()
                .map((b) => (
                  <div
                    key={b.key}
                    className={b.fillSoft}
                    style={{ height: `${(w[b.key].share ?? 0) * 100}%` }}
                  />
                ))
            ) : (
              <div className="bg-muted h-full" />
            )}
          </button>
        ))}
      </div>
      <div
        className="text-muted-foreground grid gap-3 border-t pt-1.5 text-[11px]"
        style={{
          gridTemplateColumns: `repeat(${shown.length}, minmax(0, 1fr))`,
        }}
      >
        {shown.map((w) => (
          <div key={w.from}>
            {formatDay(w.from)}
            {w.partial ? (
              <span className="opacity-70"> · {w.days}d</span>
            ) : null}
          </div>
        ))}
      </div>
      <p className="text-muted-foreground text-xs">
        A day is about ±11 points at the usual volume, so weeks are shown. Faded
        weeks have fewer than seven days.
      </p>
    </div>
  );
}

type Tab = 'yesterday' | 'last7' | 'last28';

/** The four outcomes, by window and by week, with their noise. */
export function OutcomesSection({
  report,
  onOpen,
}: {
  report: ClaireLiveQualityReport;
  onOpen: Open;
}) {
  const { buckets, outcomes } = report;
  const [tab, setTab] = useState<Tab>('last7');
  const window =
    tab === 'yesterday' && buckets.yesterday
      ? buckets.yesterday
      : tab === 'last28'
        ? buckets.last28
        : buckets.last7;
  const handoffs =
    tab === 'yesterday'
      ? outcomes.handoffs.yesterday
      : tab === 'last7'
        ? outcomes.handoffs.last7
        : null;
  const judgeChange = tab === 'last7' ? buckets.judgeChangeInLast7 : null;
  return (
    <section className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">
            {rangeText(window)} · {window.total.toLocaleString('en-GB')} replies
          </h2>
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList>
              <TabsTrigger value="yesterday" disabled={!buckets.yesterday}>
                Yesterday
              </TabsTrigger>
              <TabsTrigger value="last7">7 days</TabsTrigger>
              <TabsTrigger value="last28">28 days</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {judgeChange ? (
          <p className="text-xs text-amber-700" data-testid="judge-change-note">
            The judge changed on {formatDay(judgeChange.day)} (
            {judgeChange.label}): the two halves of this window were judged
            differently.
          </p>
        ) : null}
      </div>
      {window.total === 0 ? (
        <p className="text-muted-foreground text-sm">
          No customer messages were judged in this window.
        </p>
      ) : (
        <Buckets
          window={window}
          change={tab === 'last7' ? buckets.change7 : null}
          handoffs={handoffs}
          minJudged={outcomes.handoffs.minJudged}
          onOpen={onOpen}
        />
      )}
      <Weeks weeks={buckets.weeks} onOpen={onOpen} />
    </section>
  );
}
